import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, FormControl, InputLabel, Select, Typography, Box, Paper, IconButton, Alert
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPatients } from '../../redux/slices/patientsSlice';
import { createAppointment } from '../../redux/slices/appointmentsSlice';
import { createPatient } from '../../services/patientService';
import { FacilityService } from '../../services/facilityService';
import { generateMRN } from '../../utils/mrnUtils';
import { APPOINTMENT_TYPES } from '../../types/enums';
import { useUserData } from '../../hooks/useUserData';
import { DatePicker } from '@mui/x-date-pickers';
import DocumentScanButton from '../common/DocumentScanButton';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

const defaultPatientFields = {
  firstName: '',
  lastName: '',
  dob: '',
  sex: '',
  mobile: '',
  address: '',
  mrn: '',
};

const defaultVisitFields = {
  appointmentType: '',
  reason: '',
  department: '',
  provider: '',
  room: '',
  dateTime: '',
};

const defaultTriageFields = {
  allergiesKnown: '',
  isolation: '',
  interpreter: '',
};

const defaultInsuranceFields = {
  insuranceName: '',
  insuranceId: '',
};

const sexes = ['M', 'F', 'Other'];

export default function QuickRegistrationModal({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const patients = useSelector(state => state.patients.patients);
  const [patientFields, setPatientFields] = useState(defaultPatientFields);
  const [visitFields, setVisitFields] = useState(defaultVisitFields);
  const [triageFields, setTriageFields] = useState(defaultTriageFields);
  const [insuranceFields, setInsuranceFields] = useState(defaultInsuranceFields);
  const [setSearching] = useState(false);
  const [existingPatient, setExistingPatient] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const { defaultFacility } = useUserData();
  
  // Always use default facility
  const facilityId = defaultFacility?.id || '';

  useEffect(() => {
    if (open) {
      setPatientFields(defaultPatientFields);
      setVisitFields(defaultVisitFields);
      setTriageFields(defaultTriageFields);
      setInsuranceFields(defaultInsuranceFields);
      setExistingPatient(null);
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  // Auto-search for existing patient
  useEffect(() => {
    if (
      patientFields.firstName &&
      patientFields.lastName &&
      patientFields.dob &&
      patientFields.mobile
    ) {
      setSearching(true);
      // Simple search by name, dob, mobile
      const found = patients.find(
        p =>
          p.firstName?.toLowerCase() === patientFields.firstName.toLowerCase() &&
          p.lastName?.toLowerCase() === patientFields.lastName.toLowerCase() &&
          p.dob === patientFields.dob &&
          p.mobile === patientFields.mobile
      );
      if (found) {
        setExistingPatient(found);
        setPatientFields({
          firstName: found.firstName || '',
          lastName: found.lastName || '',
          dob: found.dob || '',
          sex: found.sex || '',
          mobile: found.mobile || '',
          address: found.address || '',
          mrn: found.mrn || '',
        });
      } else {
        setExistingPatient(null);
        setPatientFields(prev => ({ ...prev, mrn: generateMRN() }));
      }
      setSearching(false);
    }
  }, [patientFields.firstName, patientFields.lastName, patientFields.dob, patientFields.mobile, patients, setSearching]);

  // Fetch departments when facilityId changes
  useEffect(() => {
    async function fetchDepartments() {
      if (!defaultFacility?.id) { setDepartments([]); return; }
      try {
        const departments = await FacilityService.getFacilityDepartments(defaultFacility?.id);
        setDepartments(departments);
      } catch (error) {
        console.error('Error fetching facility departments:', error);
        setDepartments([]);
      }
    }
    fetchDepartments();
    setVisitFields(prev => ({ ...prev, department: '' })); // Reset department on facility change
  }, [defaultFacility?.id]);

  const handlePatientChange = e => {
    const { name, value } = e.target;
    setPatientFields(prev => ({ ...prev, [name]: value }));
  };
  const handleVisitChange = e => {
    const { name, value } = e.target;
    setVisitFields(prev => ({ ...prev, [name]: value }));
  };
  const handleTriageChange = e => {
    const { name, value } = e.target;
    setTriageFields(prev => ({ ...prev, [name]: value }));
  };
  const handleInsuranceChange = e => {
    const { name, value } = e.target;
    setInsuranceFields(prev => ({ ...prev, [name]: value }));
  };

  // Handle driver's license scan data
  const handleLicenseScanData = (data) => {
    if (!data) return;
    
    // Build full address from components
    const addressParts = [
      data.address,
      data.city,
      data.state,
      data.zipCode
    ].filter(Boolean);
    const fullAddress = addressParts.join(', ');
    
    setPatientFields(prev => ({
      ...prev,
      firstName: data.firstName || prev.firstName,
      lastName: data.lastName || prev.lastName,
      dob: data.dateOfBirth || prev.dob,
      sex: data.sex || prev.sex,
      address: fullAddress || prev.address,
    }));
  };

  // Handle insurance card scan data
  const handleInsuranceScanData = (data) => {
    if (!data) return;
    
    setInsuranceFields(prev => ({
      ...prev,
      insuranceName: data.insurerName || prev.insuranceName,
      insuranceId: data.memberId || prev.insuranceId,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      let patientId = existingPatient?.id;
      // If new patient, create patient
      if (!patientId) {
        const patientData = {
          firstName: patientFields.firstName,
          lastName: patientFields.lastName,
          dob: patientFields.dob,
          sex: patientFields.sex,
          mobile: patientFields.mobile,
          address: patientFields.address,
          mrn: patientFields.mrn,
          facilityId: facilityId, // Include facilityId for security
          insuranceName: insuranceFields.insuranceName,
          insuranceId: insuranceFields.insuranceId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const createdPatient = await createPatient(patientData);
        patientId = createdPatient.id;
        dispatch(fetchPatients(facilityId));
      }
      // Create appointment/encounter
      const appointmentData = {
        patientId,
        patientName: `${patientFields.firstName} ${patientFields.lastName}`,
        facilityId,
        appointmentType: visitFields.appointmentType,
        reason: visitFields.reason,
        department: visitFields.department,
        provider: visitFields.provider,
        room: visitFields.room,
        date: visitFields.dateTime,
        triage: triageFields,
        insurance: insuranceFields,
        status: 'scheduled',
        createdAt: new Date().toISOString(),
      };
      await dispatch(createAppointment(appointmentData)).unwrap();
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError('Failed to create patient/appointment.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Section Header component for consistent styling
  const SectionHeader = ({ icon: Icon, title, action }) => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      mb: 1.5,
      pb: 1,
      borderBottom: '2px solid #e3f2fd',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon sx={{ fontSize: 20, color: '#1976d2' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2', fontSize: '0.95rem' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: 2,
        px: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonAddIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Quick Registration + Appointment
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, backgroundColor: '#fafbfc' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <form id="quick-reg-form" onSubmit={handleSubmit}>
          {/* Patient Information Section */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: 'white' }}>
            <SectionHeader 
              icon={PersonIcon} 
              title="Patient Information"
              action={
                <DocumentScanButton
                  documentType="drivers_license"
                  onDataExtracted={handleLicenseScanData}
                  buttonLabel="Scan License"
                  buttonSize="small"
                />
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="First Name" name="firstName" value={patientFields.firstName} onChange={handlePatientChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Last Name" name="lastName" value={patientFields.lastName} onChange={handlePatientChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={6}>
                <DatePicker 
                  label="DOB" 
                  value={patientFields.dob ? new Date(patientFields.dob) : null} 
                  onChange={(date) => handlePatientChange({ target: { name: 'dob', value: date ? date.toISOString().split('T')[0] : '' } })} 
                  slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }} 
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Sex</InputLabel>
                  <Select name="sex" value={patientFields.sex} onChange={handlePatientChange} label="Sex" inputProps={{ 'aria-hidden': false, tabIndex: 0 }}>
                    <MenuItem value="">Select</MenuItem>
                    {sexes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Mobile Phone" name="mobile" value={patientFields.mobile} onChange={handlePatientChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Address (optional)" name="address" value={patientFields.address} onChange={handlePatientChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Temporary MRN" name="mrn" value={patientFields.mrn} fullWidth disabled size="small" sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#666' } }} />
              </Grid>
            </Grid>
          </Paper>

          {/* Visit Information Section */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: 'white' }}>
            <SectionHeader icon={EventNoteIcon} title="Visit Information" />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Appointment Type</InputLabel>
                  <Select name="appointmentType" value={visitFields.appointmentType} onChange={handleVisitChange} label="Appointment Type" inputProps={{ 'aria-hidden': false, tabIndex: 0 }}>
                    {APPOINTMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Reason for Visit" name="reason" value={visitFields.reason} onChange={handleVisitChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Department</InputLabel>
                  <Select name="department" value={visitFields.department} onChange={handleVisitChange} label="Department" disabled={departments.length === 0} inputProps={{ 'aria-hidden': false, tabIndex: 0 }}>
                    <MenuItem value="">{departments.length === 0 ? 'No departments available' : 'Select'}</MenuItem>
                    {departments.map(d => <MenuItem key={d.id || d.name} value={d.name}>{d.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Provider (optional)" name="provider" value={visitFields.provider} onChange={handleVisitChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Room/Bed (if assigned)" name="room" value={visitFields.room} onChange={handleVisitChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Date/Time" name="dateTime" type="datetime-local" value={visitFields.dateTime} onChange={handleVisitChange} fullWidth required size="small" InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Paper>

          {/* Triage / Flags Section */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: 'white' }}>
            <SectionHeader icon={HealthAndSafetyIcon} title="Triage / Flags" />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Allergies Known?</InputLabel>
                  <Select name="allergiesKnown" value={triageFields.allergiesKnown} onChange={handleTriageChange} label="Allergies Known?" inputProps={{ 'aria-hidden': false, tabIndex: 0 }}>
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Y">Y</MenuItem>
                    <MenuItem value="N">N</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Isolation Precautions?</InputLabel>
                  <Select name="isolation" value={triageFields.isolation} onChange={handleTriageChange} label="Isolation Precautions?" inputProps={{ 'aria-hidden': false, tabIndex: 0 }}>
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Y">Y</MenuItem>
                    <MenuItem value="N">N</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Interpreter Required?</InputLabel>
                  <Select name="interpreter" value={triageFields.interpreter} onChange={handleTriageChange} label="Interpreter Required?" inputProps={{ 'aria-hidden': false, tabIndex: 0 }}>
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Y">Y</MenuItem>
                    <MenuItem value="N">N</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Insurance Section */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', backgroundColor: 'white' }}>
            <SectionHeader 
              icon={LocalHospitalIcon} 
              title="Insurance (optional)"
              action={
                <DocumentScanButton
                  documentType="insurance_card"
                  onDataExtracted={handleInsuranceScanData}
                  buttonLabel="Scan Insurance"
                  buttonSize="small"
                />
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Insurance Name" name="insuranceName" value={insuranceFields.insuranceName} onChange={handleInsuranceChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="ID#" name="insuranceId" value={insuranceFields.insuranceId} onChange={handleInsuranceChange} fullWidth size="small" />
              </Grid>
            </Grid>
          </Paper>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafbfc', borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose} 
          disabled={submitting}
          sx={{ 
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          form="quick-reg-form" 
          variant="contained" 
          disabled={submitting}
          sx={{ 
            px: 3,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
          }}
        >
          {submitting ? 'Submitting...' : 'Check-In & Create Encounter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 